/* eslint-disable max-len */
import React, { useContext, useState } from "react";
import debounce from "lodash/debounce";

import { useModal } from "@/hooks/useModalStore";
import { useOverViewResponderCompanies } from "@/hooks/useResponderCompany";
import { ResponderCompanyCard } from "@/components/Cards/ResponderCompanyCard";
import { PlusIcon } from "lucide-react";

import { CustomIcons } from "@/assets";
import { usePanicTypes } from "@/hooks/usePanicTypes";

import { SearchOutlined } from "@ant-design/icons";
import CustomInputField from "@/components/widgets/Inputs/CustomInputField";
import Spin from "@/components/widgets/Spinner/Spin";
import HeaderComponent from "../../components/Header/Header";

import { Button } from "../../components/ui/button";
import Colors from "../../config/Colors";
import AuthContext from "@/contexts/AuthContext";

function ResponderCompany() {
  const panicTypes = usePanicTypes();
  const { isAdmin } = useContext(AuthContext);

  const { onOpen } = useModal();

  const [search, setSearch] = useState("");
  const [searchTextValue, setSearchTextValue] = useState("");

  const debounceSetGlobalFilterValue = debounce((value) => {
    setSearch(value);
  }, 1200); // 500ms delay

  const { data, hasNextPage, fetchNextPage, isLoading, isFetchingNextPage } =
    useOverViewResponderCompanies(10, search);

  const handleSearchChange = (e: string) => {
    debounceSetGlobalFilterValue(e);
    setSearchTextValue(e);
  };

  // This function will be called when the user scrolls near the bottom of the list
  // It's a simplistic way to implement infinite loading. For a real app, consider using a more robust solution like 'react-infinite-scroll-component' or observing scroll position.
  const handleScroll = (event: any) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop === clientHeight && hasNextPage) {
      fetchNextPage();
    }
  };

  const handleAddCompany = () => {
    // Add logic to handle adding a new company
    onOpen("createResponderCompany", { responderCompany: undefined });
  };

  if (isLoading && !search)
    return (
      <div className="flex items-center justify-center w-full h-full bg-backgroundColor">
        <CustomIcons.EOSThreeDotsLoading
          width={90}
          height={90}
          svgColor={Colors.appMainColor}
        />
      </div>
    );

  return (
    <div className="flex w-full h-full bg-backgroundColor">
      <div
        className="w-full h-full max-h-screen overflow-y-auto disable-scrollbars"
        onScroll={handleScroll}
      >
        <HeaderComponent title="Responder Company" />
        {isAdmin && (
          <div className="flex flex-row items-center justify-end w-full gap-3 px-3 mt-1 mb-3">
            <CustomInputField
              value={searchTextValue}
              onChange={(e: string) => handleSearchChange(e)}
              onPressEnter={() => {}}
              inputProps={{
                size: "large",
                placeholder: "Search by company name",
                allowClear: true,
                className: "ml-2 w-1/4",
                prefix: <SearchOutlined />,
              }}
              theme={{
                token: {
                  colorPrimaryHover: `${Colors.appMainColor}`,
                },
              }}
              mergeThemes
            />
            <Button
              size="icon"
              variant="ghost"
              onClick={handleAddCompany}
              className="flex items-center justify-center w-24 h-12 p-4 bg-white shadow-md cursor-pointer rounded-olg hover:bg-gray-100"
            >
              <PlusIcon className="w-6 h-6" color={Colors.appMainColor} />
            </Button>
          </div>
        )}

        <Spin spinning={isLoading} tip="searching...">
          <div className="grid items-center grid-cols-1 gap-4 px-4 md:grid-cols-2">
            {data?.pages.map((group, i) => (
              <React.Fragment key={i}>
                {group.data.map((company) => (
                  <ResponderCompanyCard key={company.id} company={company} />
                ))}
              </React.Fragment>
            ))}
          </div>
        </Spin>

        {isFetchingNextPage && (
          <div className="flex items-center justify-center w-ful">
            <CustomIcons.EOSThreeDotsLoading
              width={60}
              height={60}
              svgColor={Colors.appMainColor}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ResponderCompany;
